import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Edit, Trash2, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { getBlogs, deleteBlog, getBlogCount } from '@/services/blogApi';

export const BlogPage = () => {
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentType, setContentType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deletePost, setDeletePost] = useState(null);

  const [publishedCount, setPublishedCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  const [scheduledCount, setScheduledCount] = useState(0);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const res = await getBlogs();
      const list = res?.data || (Array.isArray(res) ? res : []);
      setPosts(list);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load posts');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    try {
      const res = await getBlogCount();
      const counts = res?.data || [];
      setPublishedCount(
        counts.find((c) => c.status === 'Published')?.count ?? 0
      );
      setDraftCount(counts.find((c) => c.status === 'Draft')?.count ?? 0);
      setScheduledCount(
        counts.find((c) => c.status === 'Scheduled')?.count ?? 0
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPosts();
    fetchCounts();
  }, []);

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const type = (post.content_type || '').toLowerCase();
      const status = (post.status || '').toLowerCase();

      const matchesType =
        contentType === 'all' || type === contentType.toLowerCase();
      const matchesStatus =
        statusFilter === 'all' || status === statusFilter.toLowerCase();
      const matchesSearch = (post.title || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      return matchesType && matchesStatus && matchesSearch;
    });
  }, [posts, contentType, statusFilter, searchTerm]);

  const handleDelete = async () => {
    if (!deletePost) return;
    try {
      await deleteBlog(deletePost.id);
      toast.success('Post deleted');
      setDeletePost(null);
      fetchPosts();
      fetchCounts();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <div>
      <PageHeader
        title="Blog & Resources"
        description="Manage blog posts and trading resources"
        breadcrumbs={['Admin', 'Content', 'Blog & Resources']}
        actions={
          <Button onClick={() => navigate('/dashboard/blog/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Post
          </Button>
        }
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <Tabs value={contentType} onValueChange={setContentType}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="blog">Blog Posts</TabsTrigger>
              <TabsTrigger value="resource">Resources</TabsTrigger>
            </TabsList>
          </Tabs>

          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList>
              <TabsTrigger value="all">All Status</TabsTrigger>
              <TabsTrigger value="published">
                Published
                <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                  {publishedCount}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="draft">
                Draft
                <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                  {draftCount}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="scheduled">
                Scheduled
                <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                  {scheduledCount}
                </Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Title
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Type
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Category
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Author
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Status
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Date
                      </TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPosts.map((post) => (
                      <TableRow key={post.id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-400" />
                            <p className="text-sm font-medium text-slate-900 max-w-md truncate">
                              {post.title}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {post.content_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{post.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback>
                                {post.author?.[0] || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm text-slate-700">
                              {post.author}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={post.status} />
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">
                            {post.created_at
                              ? format(new Date(post.created_at), 'MMM dd, yyyy')
                              : '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                navigate(`/dashboard/blog/${post.id}/edit`)
                              }
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600"
                              onClick={() => setDeletePost(post)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {filteredPosts.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No posts found</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!deletePost}
        onOpenChange={() => setDeletePost(null)}
        title="Delete Post"
        description={`Are you sure you want to delete "${deletePost?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
};